import type { Tooltip } from "@base-ui/react/tooltip"
import { useEffect, useRef, useState, type FocusEvent } from "react"

/*
 * A tooltip on a button that opens a popover, as a Picker's PropertyButton and the Filter button
 * are. While the popover is open the tooltip stays shut, and when the popover closes, by Esc or by
 * a choice, the focus it hands back to its button opens nothing: the tooltip would cover what the
 * popover just changed, and take the next Esc, which the page means for clearing its filters. The
 * pointer arriving on the button, or focus leaving it, lets the tooltip open again as it would.
 */

/**
 * The controlled `open` and `onOpenChange` for the Tooltip (`tooltip`), and the handlers for its
 * trigger (`trigger`), given whether the button's popover is open now.
 */
export function useTriggerTooltip(popupOpen: boolean) {
  const [open, setOpen] = useState(false)
  // The popover has been open since the button last lost focus or met the pointer, so the next
  // focus it gets is the one the popover hands back as it closes.
  const handedBack = useRef(false)
  useEffect(() => {
    if (popupOpen) handedBack.current = true
  }, [popupOpen])
  const settle = () => (handedBack.current = false)
  return {
    tooltip: {
      open: open && !popupOpen,
      onOpenChange: (next: boolean, { reason }: Tooltip.Root.ChangeEventDetails) => {
        if (next && reason === "trigger-focus" && handedBack.current) return
        setOpen(next)
      },
    },
    trigger: {
      onPointerEnter: settle,
      // Opening the popover takes focus into it, which isn't the button being left.
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (event.currentTarget.getAttribute("aria-expanded") !== "true") settle()
      },
    },
  }
}
